import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Share2, Copy, Check, X, Clock, Eye, Trash2, Link2 } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const ShareDemoModal = ({ token, onClose }) => {
  const [label, setLabel] = useState("");
  const [expiresHours, setExpiresHours] = useState(24);
  const [maxViews, setMaxViews] = useState(10);
  const [createdLink, setCreatedLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [existingLinks, setExistingLinks] = useState([]);
  const [showExisting, setShowExisting] = useState(false);

  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchLinks();
  }, []);

  const fetchLinks = async () => {
    try {
      const res = await axios.get(`${API}/demo-links/list`, { headers: authHeaders });
      setExistingLinks(res.data);
    } catch (e) {}
  };

  const handleCreate = async () => {
    setIsCreating(true);
    try {
      const res = await axios.post(`${API}/demo-links/create`, {
        label,
        expires_hours: expiresHours,
        max_views: maxViews,
      }, { headers: authHeaders });
      setCreatedLink(res.data);
      fetchLinks();
    } catch (e) {
      console.error("Failed to create demo link", e);
    }
    setIsCreating(false);
  };

  const handleCopy = () => {
    const fullUrl = `${window.location.origin}/demo/${createdLink.token}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRevoke = async (linkId) => {
    try {
      await axios.delete(`${API}/demo-links/${linkId}`, { headers: authHeaders });
      fetchLinks();
    } catch (e) {}
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="share-demo-modal">
      <div className="w-full max-w-lg bg-[#1a1d22] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#F97316]" />
            <h2 className="font-heading text-lg font-bold text-white">Share Demo Link</h2>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {!createdLink ? (
            <>
              {/* Create New Link */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/60 text-sm">Label (optional)</Label>
                  <Input
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Board Meeting 14 Feb"
                    className="bg-[#121212] border-white/20 text-white placeholder:text-white/30"
                    data-testid="demo-link-label"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Expires after
                    </Label>
                    <select
                      value={expiresHours}
                      onChange={(e) => setExpiresHours(Number(e.target.value))}
                      className="w-full h-10 px-3 rounded-md bg-[#121212] border border-white/20 text-white text-sm"
                      data-testid="demo-link-expiry"
                    >
                      <option value={1}>1 hour</option>
                      <option value={4}>4 hours</option>
                      <option value={24}>24 hours</option>
                      <option value={72}>3 days</option>
                      <option value={168}>7 days</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white/60 text-sm flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Max views
                    </Label>
                    <select
                      value={maxViews}
                      onChange={(e) => setMaxViews(Number(e.target.value))}
                      className="w-full h-10 px-3 rounded-md bg-[#121212] border border-white/20 text-white text-sm"
                      data-testid="demo-link-max-views"
                    >
                      <option value={1}>1 view (single use)</option>
                      <option value={3}>3 views</option>
                      <option value={5}>5 views</option>
                      <option value={10}>10 views</option>
                      <option value={25}>25 views</option>
                    </select>
                  </div>
                </div>

                {/* Security Note */}
                <div className="p-3 rounded-lg bg-[#F97316]/10 border border-[#F97316]/20">
                  <div className="text-[#F97316] text-xs font-semibold mb-1">IP PROTECTION</div>
                  <div className="text-white/60 text-xs leading-relaxed">
                    Every view is logged with timestamp. Dynamic watermark with viewer identity is embedded across all screens. 
                    Copy, print, and screen capture protections are active.
                  </div>
                </div>
              </div>

              <Button
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full h-11 bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white font-bold"
                data-testid="create-demo-link-btn"
              >
                {isCreating ? "Creating..." : "Generate Secure Link"}
              </Button>
            </>
          ) : (
            <>
              {/* Link Created */}
              <div className="text-center space-y-4">
                <div className="w-14 h-14 rounded-full bg-[#10B981]/20 flex items-center justify-center mx-auto">
                  <Check className="w-7 h-7 text-[#10B981]" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Link Created</h3>
                  <p className="text-white/40 text-sm mt-1">{createdLink.label}</p>
                </div>

                <div className="flex items-center gap-2 bg-[#121212] border border-white/20 rounded-lg p-3">
                  <Link2 className="w-4 h-4 text-[#F97316] flex-shrink-0" />
                  <span className="text-white text-sm truncate flex-1">
                    {window.location.origin}/demo/{createdLink.token}
                  </span>
                  <Button
                    onClick={handleCopy}
                    size="sm"
                    className={`flex-shrink-0 ${copied ? 'bg-[#10B981]' : 'bg-[#F97316]'} text-white`}
                    data-testid="copy-demo-link-btn"
                  >
                    {copied ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3 text-left">
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="text-white/40 text-xs">Expires</div>
                    <div className="text-white text-sm font-semibold">
                      {new Date(createdLink.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="text-white/40 text-xs">Max Views</div>
                    <div className="text-white text-sm font-semibold">{createdLink.max_views}</div>
                  </div>
                </div>

                <Button
                  onClick={() => setCreatedLink(null)}
                  variant="ghost"
                  className="text-white/40 hover:text-white"
                >
                  Create Another
                </Button>
              </div>
            </>
          )}

          {/* Existing Links */}
          {existingLinks.length > 0 && (
            <div className="border-t border-white/10 pt-4">
              <button
                onClick={() => setShowExisting(!showExisting)}
                className="text-white/40 hover:text-white/60 text-sm flex items-center gap-1 transition-colors"
              >
                Active links ({existingLinks.filter(l => l.is_active).length})
              </button>
              
              {showExisting && (
                <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                  {existingLinks.map((link) => (
                    <div key={link.link_id} className="flex items-center justify-between p-2 rounded-lg bg-white/5 border border-white/10">
                      <div className="flex-1 min-w-0">
                        <div className="text-white text-xs font-semibold truncate">{link.label}</div>
                        <div className="text-white/30 text-[10px]">
                          {link.views_used}/{link.max_views} views · {link.is_active ? "Active" : "Expired"}
                        </div>
                      </div>
                      {link.is_active && (
                        <button
                          onClick={() => handleRevoke(link.link_id)}
                          className="text-red-400/60 hover:text-red-400 ml-2 transition-colors"
                          data-testid={`revoke-link-${link.link_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareDemoModal;
